/**
 * ProfileFormModal - Create/Edit profile modal with live preview
 *
 * Two-column layout: configuration on left, live preview on right.
 */

import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Info, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ModuleSelector } from './ModuleSelector';
import { WidgetSelector } from './WidgetSelector';
import { ProfilePreview } from './ProfilePreview';
import { ModuleDependencyEditor } from './ModuleDependencyEditor';
import { TabOverrideEditor } from './TabOverrideEditor';
import { useSidebarPreview } from '../../hooks/useSidebarPreview.js';

// Available icons for profiles — codes only, labels resolved at render via t('uiProfiles.form.icons.<id>')
const ICON_OPTIONS = [
  { id: 'Building2' },
  { id: 'UserCog' },
  { id: 'BarChart3' },
  { id: 'UserCircle' },
  { id: 'Table2' },
  { id: 'Eye' },
];

// Navigation style options — codes only, labels/descriptions resolved at render (piège #1)
const NAV_STYLE_OPTIONS = [
  { id: 'minimal' },
  { id: 'grouped' },
  { id: 'full' },
];

// Default form values for new profile (V3)
const DEFAULT_FORM = {
  code: '',
  name: '',
  description: '',
  color: '#3b82f6',
  icon: 'UserCog',
  nav_style: 'grouped',
  max_menu_depth: 2,
  modules: ['dashboard'],
  widgets: ['alerts_priority', 'vehicles_status', 'quick_actions'],
  custom_group_labels: {},
  module_dependencies: {},
  tab_overrides: {},
  read_only: false,
  quick_actions: [],
  is_default: false,
  status: 'active',
};

export function ProfileFormModal({ profile, onSave, onClose }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { resolvedGroups, resolve } = useSidebarPreview();

  // Initialize form with profile data if editing
  useEffect(() => {
    if (profile) {
      // Parse JSON fields if they're strings
      let customLabels = profile.custom_group_labels;
      let moduleDeps = profile.module_dependencies;
      if (typeof customLabels === 'string') {
        try { customLabels = JSON.parse(customLabels); } catch { customLabels = {}; }
      }
      if (typeof moduleDeps === 'string') {
        try { moduleDeps = JSON.parse(moduleDeps); } catch { moduleDeps = {}; }
      }

      // Parse V3 JSON fields
      let tabOverrides = profile.tab_overrides;
      let quickActions = profile.quick_actions;
      if (typeof tabOverrides === 'string') {
        try { tabOverrides = JSON.parse(tabOverrides); } catch { tabOverrides = {}; }
      }
      if (typeof quickActions === 'string') {
        try { quickActions = JSON.parse(quickActions); } catch { quickActions = []; }
      }

      setFormData({
        code: profile.code || '',
        name: profile.name || '',
        description: profile.description || '',
        color: profile.color || '#3b82f6',
        icon: profile.icon || 'UserCog',
        nav_style: profile.nav_style || 'grouped',
        max_menu_depth: profile.max_menu_depth || 2,
        modules: Array.isArray(profile.modules) ? profile.modules : ['dashboard'],
        widgets: Array.isArray(profile.widgets) ? profile.widgets : [],
        custom_group_labels: customLabels || {},
        module_dependencies: moduleDeps || {},
        tab_overrides: tabOverrides || {},
        read_only: profile.read_only === 1 || profile.read_only === true || false,
        quick_actions: Array.isArray(quickActions) ? quickActions : [],
        is_default: profile.is_default || false,
        status: profile.status || 'active',
      });
    }
  }, [profile]);

  // Trigger live sidebar preview when modules change
  useEffect(() => {
    if (formData.code) {
      resolve({ profileCode: formData.code, mode: 'elusive' });
    }
  }, [formData.modules, formData.code, resolve]);

  // Update form field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is edited
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = t('uiProfiles.form.errors.codeRequired');
    } else if (!/^[a-z0-9_]+$/.test(formData.code)) {
      newErrors.code = t('uiProfiles.form.errors.codeFormat');
    }

    if (!formData.name.trim()) {
      newErrors.name = t('uiProfiles.form.errors.nameRequired');
    }

    if (formData.modules.length === 0) {
      newErrors.modules = t('uiProfiles.form.errors.modulesRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSaving(true);
    try {
      await onSave(formData);
    } catch (err) {
      setErrors({ submit: err.message || t('uiProfiles.form.errors.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!profile;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? t('uiProfiles.form.titleEdit', { name: profile.name }) : t('uiProfiles.form.titleCreate')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Error banner */}
        {errors.submit && (
          <div className="mx-4 mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-500 dark:text-red-400">
            <AlertCircle size={18} />
            <span>{errors.submit}</span>
          </div>
        )}

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex">
          {/* Left: Configuration */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 border-r border-gray-200 dark:border-slate-700">
            {/* Basic info */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">{t('uiProfiles.form.basicInfo.title')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-slate-400 mb-1">
                    {t('uiProfiles.form.basicInfo.code')} *
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => updateField('code', e.target.value.toLowerCase())}
                    placeholder={t('uiProfiles.form.basicInfo.codePlaceholder')}
                    disabled={isEditing && profile.is_system}
                    className={`w-full px-3 py-2 bg-gray-100 dark:bg-slate-700 border rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
                      errors.code ? 'border-red-500' : 'border-gray-300 dark:border-slate-600'
                    }`}
                  />
                  {errors.code && (
                    <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.code}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-slate-400 mb-1">
                    {t('uiProfiles.form.basicInfo.name')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder={t('uiProfiles.form.basicInfo.namePlaceholder')}
                    className={`w-full px-3 py-2 bg-gray-100 dark:bg-slate-700 border rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300 dark:border-slate-600'
                    }`}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.name}</p>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm text-gray-500 dark:text-slate-400 mb-1">
                  {t('uiProfiles.form.basicInfo.description')}
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder={t('uiProfiles.form.basicInfo.descriptionPlaceholder')}
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </section>

            {/* Appearance */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">{t('uiProfiles.form.appearance.title')}</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-slate-400 mb-1">{t('uiProfiles.form.appearance.color')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => updateField('color', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => updateField('color', e.target.value)}
                      className="flex-1 px-3 py-2 bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-slate-400 mb-1">{t('uiProfiles.form.appearance.icon')}</label>
                  <select
                    value={formData.icon}
                    onChange={(e) => updateField('icon', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {ICON_OPTIONS.map(opt => (
                      <option key={opt.id} value={opt.id}>{t(`uiProfiles.form.icons.${opt.id}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-slate-400 mb-1">{t('uiProfiles.form.appearance.navStyle')}</label>
                  <select
                    value={formData.nav_style}
                    onChange={(e) => updateField('nav_style', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {NAV_STYLE_OPTIONS.map(opt => (
                      <option key={opt.id} value={opt.id}>{t(`uiProfiles.form.navStyles.${opt.id}.label`)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            {/* Modules */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">{t('uiProfiles.form.modules.title')}</h3>
              {errors.modules && (
                <p className="text-xs text-red-500 dark:text-red-400 mb-2">{errors.modules}</p>
              )}
              <ModuleSelector
                selectedModules={formData.modules}
                onChange={(modules) => updateField('modules', modules)}
              />
            </section>

            {/* Widgets */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">{t('uiProfiles.form.widgets.title')}</h3>
              <WidgetSelector
                selectedWidgets={formData.widgets}
                onChange={(widgets) => updateField('widgets', widgets)}
              />
            </section>

            {/* Sidebar Builder Notice */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">{t('uiProfiles.form.sidebarNotice.title')}</h3>
              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg flex items-start gap-3">
                <Info size={18} className="text-purple-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm text-purple-300 dark:text-purple-300 font-medium">
                    {t('uiProfiles.admin.tabs.sidebarBuilder')}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    {t('uiProfiles.form.sidebarNotice.body', { tabName: t('uiProfiles.admin.tabs.sidebarBuilder') })}
                  </p>
                </div>
              </div>
            </section>

            {/* Module Dependencies */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">{t('uiProfiles.form.dependencies.title')}</h3>
              <ModuleDependencyEditor
                dependencies={formData.module_dependencies}
                onChange={(deps) => updateField('module_dependencies', deps)}
                selectedModules={formData.modules}
              />
            </section>

            {/* V3: Read-Only Mode */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Eye size={16} className="text-gray-400" />
                {t('uiProfiles.form.readOnly.title')}
              </h3>
              <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 dark:bg-slate-700/30 rounded-lg border border-gray-200 dark:border-slate-600">
                <input
                  type="checkbox"
                  checked={formData.read_only}
                  onChange={(e) => updateField('read_only', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-slate-500 bg-gray-100 dark:bg-slate-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                />
                <div>
                  <div className="text-sm text-gray-900 dark:text-white">{t('uiProfiles.form.readOnly.label')}</div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">
                    {t('uiProfiles.form.readOnly.hint')}
                  </div>
                </div>
              </label>
            </section>

            {/* V3: Tab Overrides */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">{t('uiProfiles.form.tabOverrides.title')}</h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                {t('uiProfiles.form.tabOverrides.hint')}
              </p>
              <TabOverrideEditor
                tabOverrides={formData.tab_overrides}
                onChange={(overrides) => updateField('tab_overrides', overrides)}
                selectedModules={formData.modules}
              />
            </section>

            {/* Options */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">{t('uiProfiles.form.options.title')}</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => updateField('is_default', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-500 bg-gray-100 dark:bg-slate-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <div>
                    <div className="text-sm text-gray-900 dark:text-white">{t('uiProfiles.form.options.defaultLabel')}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                      {t('uiProfiles.form.options.defaultHint')}
                    </div>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.status === 'active'}
                    onChange={(e) => updateField('status', e.target.checked ? 'active' : 'inactive')}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-500 bg-gray-100 dark:bg-slate-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <div>
                    <div className="text-sm text-gray-900 dark:text-white">{t('uiProfiles.form.options.activeLabel')}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                      {t('uiProfiles.form.options.activeHint')}
                    </div>
                  </div>
                </label>
              </div>
            </section>
          </div>

          {/* Right: Live Preview */}
          <div className="w-80 bg-gray-50 dark:bg-slate-850 p-6 overflow-y-auto">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-4">{t('uiProfiles.form.preview.title')}</h3>
            <ProfilePreview formData={formData} resolvedSidebar={resolvedGroups} />
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white transition-colors"
          >
            {t('common.actions.cancel')}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Save size={18} />
            {saving ? t('common.states.saving') : t('common.actions.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProfileFormModal;
